/**
 * One project of a workspace, as a form (§5.25): where it is cloned from, where
 * it goes, how it is run, and its environment — with the API console below it
 * as JSON, which is v1's editor for a console written outside the app.
 *
 * The clone URL is the project's identity: a member who already has a
 * checkout is matched on it, and one who has not is cloned from it. So the form
 * shows what it reads as — `github.com/acme/api` — and says so when the URL
 * would not be kept as typed, which is what happens to a credential in it.
 */
import { useState } from 'react';
import type React from 'react';
import { normalizeRemote } from '@airlock/contracts/workspace/remote';
import { envSecretPath } from '@airlock/contracts/workspace/secrets';
import { ENV_FILE_MODES, PROJECT_KINDS, RUNTIME_KINDS, workspaceColorAt } from '@airlock/contracts/workspace/vocabulary';
import { color, font, radius } from '../../theme/tokens';
import { Card, Field, GhostButton, Input, RowAction } from '../../components/primitives';
import type { DocProject, SecretPresence, SecretWrites } from '../../lib/types';
import { SettingToggle } from '../../components/settings-row';
import { ColorPicker, ProblemList, newRowId, selectStyle } from './shared';

export function newRepo(order: number): DocProject {
  return {
    id: newRowId('p'),
    name: '',
    remote: '',
    host: 'other',
    cloneUrl: '',
    relPath: '',
    defaultBranch: 'main',
    runtime: 'unknown',
    kind: 'unknown',
    packageManager: null,
    installCmd: null,
    startCmd: null,
    checkCmd: null,
    healthCheck: null,
    port: null,
    portEnv: null,
    tunnelDomain: null,
    tunnelAuto: false,
    color: workspaceColorAt(order),
    envFile: 'auto',
    sortOrder: order,
    env: [],
    api: { folders: [], requests: [], environments: [], variables: [], flows: [] }
  };
}

/** An optional command or name: an empty field means "none", which the document spells `null`. */
const nullable = (value: string): string | null => (value.trim() === '' ? null : value);

export function ProjectEditor({
  repo,
  problems,
  onChange,
  onRemove,
  presence,
  writes,
  onWrites,
  endToEnd = false,
  open,
  onToggle,
  draft
}: {
  repo: DocProject;
  problems: string[];
  onChange: (next: DocProject) => void;
  onRemove: () => void;
  /** Which of the workspace's secrets have a value — never what it is. */
  presence: SecretPresence[];
  /** Secret values typed on this page, carried by the next publish. */
  writes: SecretWrites;
  onWrites: (next: SecretWrites) => void;
  /** The organization seals its values end to end, so none is typed here (`EnvRows`). */
  endToEnd?: boolean;
  /** Whether the card is unfolded under its row (§46.2). */
  open: boolean;
  onToggle: () => void;
  /** How the draft differs from what is published: a new project or an edited one. */
  draft?: 'new' | 'edited';
}): React.JSX.Element {
  const identity = repo.cloneUrl ? normalizeRemote(repo.cloneUrl) : null;
  const set = (patch: Partial<DocProject>): void => onChange({ ...repo, ...patch });
  const id = (field: string): string => `repo-${repo.id}-${field}`;

  const text = (field: keyof DocProject, label: string, placeholder = '', mono = false): React.JSX.Element => (
    <Field label={label} htmlFor={id(field)}>
      <Input
        id={id(field)}
        value={(repo[field] as string | null) ?? ''}
        placeholder={placeholder}
        onChange={(event) => set({ [field]: nullable(event.target.value) } as Partial<DocProject>)}
        style={mono ? { fontFamily: font.mono, fontSize: 13 } : undefined}
      />
    </Field>
  );

  const select = <T extends string>(field: keyof DocProject, label: string, options: readonly T[]): React.JSX.Element => (
    <Field label={label} htmlFor={id(field)}>
      <select
        id={id(field)}
        value={repo[field] as string}
        onChange={(event) => set({ [field]: event.target.value } as Partial<DocProject>)}
        style={selectStyle}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </Field>
  );

  // The row's summary (§46.2): what it runs, what it is, its port and how many
  // variables — read without opening the card.
  const summary = [
    repo.runtime,
    repo.kind,
    repo.port ? `port ${repo.port}` : null,
    repo.env.length ? `${repo.env.length} variable${repo.env.length === 1 ? '' : 's'}` : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div style={{ borderTop: `1px solid ${color.line}` }}>
      {/* One row per project (§46.2): the whole row opens and closes its card —
          chevron, colour, name, id, the draft's label, the repository read in
          mono, the summary, and the error count in red, so an error is seen
          without opening anything. */}
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        style={{
          width: '100%',
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: '8px 4px',
          border: 0,
          background: 'transparent',
          fontFamily: 'inherit',
          textAlign: 'left',
          cursor: 'pointer'
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke={color.inkSubtle}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flex: 'none', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms ease' }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
        <span style={{ width: 10, height: 10, borderRadius: 999, background: repo.color, flex: 'none' }} />
        <span style={{ fontSize: 13.5, fontWeight: 600, color: color.ink }}>{repo.name || 'New project'}</span>
        <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>{repo.id}</span>
        {draft && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 20,
              padding: '0 7px',
              borderRadius: 6,
              background: color.warnBg,
              color: color.warnText,
              fontFamily: font.mono,
              fontSize: 10.5,
              letterSpacing: '.16em',
              textTransform: 'uppercase'
            }}
          >
            {draft === 'new' ? 'new · draft' : 'edited · draft'}
          </span>
        )}
        {identity && <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkMuted }}>{identity.remote}</span>}
        <span style={{ fontSize: 13, color: color.inkSubtle }}>{summary}</span>
        <span style={{ flex: 1 }} />
        {problems.length > 0 && (
          <span style={{ fontSize: 13, color: color.badText }}>
            {problems.length === 1 ? '1 error' : `${problems.length} errors`}
          </span>
        )}
      </button>
      {open && (
        <Card style={{ marginLeft: 34, marginBottom: 16 }}>
          {/* Three groups on a grid of 12, aligned on the workspace card
              (§46.3): where it comes from, how it runs, how it is reached. */}
          <Group title="SOURCE">
            <Cell span={8}>
              <Field label="Name" htmlFor={id('name')}>
                <Input id={id('name')} value={repo.name} placeholder="api" onChange={(event) => set({ name: event.target.value })} />
              </Field>
            </Cell>
            <Cell span={4}>
              <Field label="Color">
                <ColorPicker value={repo.color} label={`Color of ${repo.name || 'this project'}`} onChange={(next) => set({ color: next })} />
              </Field>
            </Cell>
            <Cell span={8}>
              <Field label="Clone URL" htmlFor={id('cloneUrl')}>
                <Input
                  id={id('cloneUrl')}
                  value={repo.cloneUrl}
                  placeholder="git@github.com:acme/api.git"
                  onChange={(event) => {
                    const next = normalizeRemote(event.target.value);
                    set({ cloneUrl: event.target.value, remote: next?.remote ?? '', host: next?.host ?? 'other' });
                  }}
                  style={{ fontFamily: font.mono, fontSize: 13 }}
                />
                <div style={{ fontSize: 12, marginTop: 5, lineHeight: 1.5, color: identity ? color.inkFaint : color.warnText }}>
                  {!repo.cloneUrl
                    ? 'Where a member without a checkout clones it from.'
                    : !identity
                      ? 'Not a remote another machine could clone — push the project somewhere first.'
                      : identity.cloneUrl !== repo.cloneUrl.trim()
                        ? `Reads as ${identity.remote}. Stored as ${identity.cloneUrl} — a credential in a URL is not kept.`
                        : `Reads as ${identity.remote}.`}
                </div>
              </Field>
            </Cell>
            <Cell span={4}>
              <Field label="Default branch" htmlFor={id('defaultBranch')}>
                <Input
                  id={id('defaultBranch')}
                  value={repo.defaultBranch}
                  onChange={(event) => set({ defaultBranch: event.target.value })}
                  style={{ fontFamily: font.mono, fontSize: 13 }}
                />
              </Field>
            </Cell>
            <Cell span={6}>{text('relPath', 'Folder under the one each member picks', 'services/api', true)}</Cell>
            <Cell span={6}>
              {/* One project of several in one repository (§5.4): absent — not null — is the repository itself. */}
              <Field label="Folder inside the repository" htmlFor={id('subPath')}>
                <Input
                  id={id('subPath')}
                  value={repo.subPath ?? ''}
                  placeholder="empty for the repository itself — or site, apps/web"
                  onChange={(event) => {
                    const { subPath: _dropped, ...rest } = repo;
                    const next = event.target.value.trim();
                    onChange(next ? { ...rest, subPath: next } : rest);
                  }}
                  style={{ fontFamily: font.mono, fontSize: 13 }}
                />
                <div style={{ fontSize: 12, marginTop: 5, lineHeight: 1.5, color: color.inkFaint }}>
                  {repo.subPath
                    ? 'A project inside a repository this workspace also holds, on the same clone URL. Members get it with that repository and run its commands in this folder.'
                    : 'For a monorepo: one project per folder that has its own commands, beside the repository itself.'}
                </div>
              </Field>
            </Cell>
          </Group>

          <Group title="RUN">
            <Cell span={4}>{select('runtime', 'Runtime', RUNTIME_KINDS)}</Cell>
            <Cell span={4}>{select('kind', 'Serves', PROJECT_KINDS)}</Cell>
            <Cell span={4}>{text('packageManager', 'Package manager', 'pnpm', true)}</Cell>
            <Cell span={4}>{text('installCmd', 'Install', 'pnpm install', true)}</Cell>
            <Cell span={4}>{text('startCmd', 'Start', 'pnpm dev', true)}</Cell>
            <Cell span={4}>{text('checkCmd', 'Check', 'pnpm check', true)}</Cell>
            <Cell span={4}>{select('envFile', 'Environment file', ENV_FILE_MODES)}</Cell>
            <Cell span={8}>
              {/* On is the key's absence: only an off is written, so the document
                  still reads in an app that predates it (`DocProject`). */}
              <label style={{ display: 'flex', gap: 10, alignItems: 'center', height: '100%', paddingTop: 22, fontSize: 13.5, color: color.inkBody }}>
                <SettingToggle
                  on={repo.skipUnchangedInstall !== false}
                  onToggle={() => {
                    const { skipUnchangedInstall, ...rest } = repo;
                    onChange(skipUnchangedInstall === false ? rest : { ...rest, skipUnchangedInstall: false });
                  }}
                />
                Skip the install when its dependencies are unchanged
              </label>
            </Cell>
          </Group>

          <Group title="NETWORK">
            <Cell span={4}>{text('port', 'Port', '3000', true)}</Cell>
            <Cell span={4}>{text('portEnv', 'Port variable', 'PORT', true)}</Cell>
            <Cell span={4}>{text('healthCheck', 'Health check', 'curl -fsS localhost:3000/health', true)}</Cell>
            <Cell span={6}>{text('tunnelDomain', 'Reserved tunnel domain', 'api-acme.ngrok.app', true)}</Cell>
            <Cell span={6}>
              {/* A switch, as every on/off of the page is (§46.3): the label
                  around it names it and a click on the words turns it too. */}
              <label style={{ display: 'flex', gap: 10, alignItems: 'center', height: '100%', paddingTop: 22, fontSize: 13.5, color: color.inkBody }}>
                <SettingToggle on={repo.tunnelAuto} onToggle={() => set({ tunnelAuto: !repo.tunnelAuto })} />
                Open its tunnel whenever it starts
              </label>
            </Cell>
          </Group>

          <EnvRows repo={repo} onChange={onChange} presence={presence} writes={writes} onWrites={onWrites} endToEnd={endToEnd} />

          {/* No API console here for the moment. It was the one part of a project
              edited as raw JSON, and a form is what the rest of this page is; the
              document still carries `api` untouched — it travels through this
              editor and out with the next publish, so a console configured in the
              app is neither shown nor lost. */}

          <ProblemList problems={problems} />
          <div style={{ display: 'flex', marginTop: 18 }}>
            <GhostButton size="sm" onClick={onRemove}>
              Remove project
            </GhostButton>
          </div>
        </Card>
      )}
    </div>
  );
}

/** A group of the project card: its kicker, then a grid of 12 (§46.3). */
function Group({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section style={{ marginTop: 18 }}>
      <div style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', color: color.inkFaint, marginBottom: 10 }}>
        {title}
      </div>
      <div className="al-grid12" style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 14 }}>
        {children}
      </div>
    </section>
  );
}

function Cell({ span, children }: { span: number; children: React.ReactNode }): React.JSX.Element {
  return <div style={{ gridColumn: `span ${span}`, minWidth: 0 }}>{children}</div>;
}

/**
 * The environment, one row per variable: its name, its value, and its kind —
 * a variable written in the document, or a secret sealed apart from it (the
 * owner's ask, 2026-10-01: the same choice and the same Show / Hide as the
 * app's Repositories page).
 *
 * A secret's value is typed here and carried by the next publish, exactly as
 * the Secrets section carries it; this page can still never read one back
 * (§5.25), so a stored value is drawn as dots and Show reveals only what was
 * typed over it.
 *
 * Under end-to-end encryption there is no field: a value is sealed in Airlock
 * on a member's machine, and the account area refuses one typed here. Turning
 * a variable into a secret then drops what was written in it with the publish,
 * and the row says where the value is set.
 */
function EnvRows({
  repo,
  onChange,
  presence,
  writes,
  onWrites,
  endToEnd
}: {
  repo: DocProject;
  onChange: (next: DocProject) => void;
  presence: SecretPresence[];
  writes: SecretWrites;
  onWrites: (next: SecretWrites) => void;
  endToEnd: boolean;
}): React.JSX.Element {
  const update = (index: number, patch: Partial<DocProject['env'][number]>): void =>
    onChange({ ...repo, env: repo.env.map((env, at) => (at === index ? { ...env, ...patch } : env)) });
  const stored = new Set(presence.map((secret) => secret.path));
  const pathOf = (key: string): string => envSecretPath(repo.id, key);
  const pending = (key: string): string | null | undefined =>
    Object.prototype.hasOwnProperty.call(writes, pathOf(key)) ? writes[pathOf(key)] : undefined;

  /** A typed secret staged for the next publish; an empty field stages nothing. */
  const stage = (key: string, value: string): void => {
    const next = { ...writes };
    if (value) next[pathOf(key)] = value;
    else delete next[pathOf(key)];
    onWrites(next);
  };

  const setKind = (index: number, secret: boolean): void => {
    const env = repo.env[index];
    if (!env || env.isSecret === secret) return;
    const path = pathOf(env.key);
    const next = { ...writes };
    if (secret) {
      // What was written as a variable becomes the secret's value, and leaves
      // the document with this publish — unless values are sealed end to end,
      // where this page cannot send one and it simply leaves.
      if (env.value && !endToEnd) next[path] = env.value;
      update(index, { isSecret: true, value: null });
    } else {
      // A value typed here comes back as the variable's; a stored one cannot
      // be read, so it is removed with this publish and the field starts empty.
      const typed = typeof writes[path] === 'string' ? (writes[path] as string) : '';
      if (stored.has(path)) next[path] = null;
      else delete next[path];
      update(index, { isSecret: false, value: typed });
    }
    onWrites(next);
  };

  const rename = (index: number, key: string): void => {
    const env = repo.env[index];
    if (!env) return;
    // A value typed for a secret follows its name; one already sealed stays
    // under the old name until the Secrets section removes it.
    const typed = env.isSecret ? pending(env.key) : undefined;
    if (typeof typed === 'string') {
      const next = { ...writes };
      delete next[pathOf(env.key)];
      if (key) next[pathOf(key)] = typed;
      onWrites(next);
    }
    update(index, { key });
  };

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', color: color.inkFaint }}>ENVIRONMENT VARIABLES</span>
        <span style={{ flex: 1 }} />
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {repo.env.map((env, index) => (
          // The card's grid of 12 (§46.3): name 1–4, value 5–9, kind 10–11,
          // Remove in the last; on a phone each takes the whole width.
          <div
            key={index}
            className="al-grid12"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 8, alignItems: 'center' }}
          >
            <Cell span={4}>
              <Input
                aria-label="Variable name"
                value={env.key}
                placeholder="DATABASE_URL"
                onChange={(event) => rename(index, event.target.value)}
                style={{ fontFamily: font.mono, fontSize: 13, padding: '8px 10px', borderRadius: radius.md }}
              />
            </Cell>
            <Cell span={5}>
              {env.isSecret && endToEnd ? (
                <Input
                  aria-label={`Value of ${env.key || 'this secret'}`}
                  disabled
                  value=""
                  placeholder={stored.has(pathOf(env.key)) ? '•••••••••• sealed end to end — change it in Airlock' : 'Set it in Airlock — sealed end to end'}
                  style={{ fontFamily: font.mono, fontSize: 13, padding: '8px 10px', borderRadius: radius.md }}
                />
              ) : env.isSecret ? (
                <SecretValue
                  name={env.key}
                  typed={pending(env.key)}
                  stored={stored.has(pathOf(env.key))}
                  onType={(value) => stage(env.key, value)}
                />
              ) : (
                <Input
                  aria-label="Value"
                  value={env.value ?? ''}
                  onChange={(event) => update(index, { value: event.target.value })}
                  style={{ fontFamily: font.mono, fontSize: 13, padding: '8px 10px', borderRadius: radius.md }}
                />
              )}
            </Cell>
            <Cell span={2}>
              <select
                aria-label={`Kind of ${env.key || 'this variable'}`}
                value={env.isSecret ? 'secret' : 'variable'}
                onChange={(event) => setKind(index, event.target.value === 'secret')}
                style={{ ...selectStyle, fontSize: 13, padding: '8px 10px', borderRadius: radius.md }}
              >
                <option value="variable">Variable</option>
                <option value="secret">Secret</option>
              </select>
            </Cell>
            <Cell span={1}>
              <RowAction danger onClick={() => onChange({ ...repo, env: repo.env.filter((_, at) => at !== index) })}>
                Remove
              </RowAction>
            </Cell>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <GhostButton
          size="sm"
          onClick={() => onChange({ ...repo, env: [...repo.env, { key: '', value: '', isSecret: false, scope: 'both' }] })}
        >
          Add a variable
        </GhostButton>
      </div>
    </div>
  );
}

/**
 * A secret's value: hidden as a password field hides one, with Show / Hide
 * inside it. What it shows is what was typed on this page — a value already
 * sealed is drawn as dots and is never sent to the page to be shown.
 */
function SecretValue({
  name,
  typed,
  stored,
  onType
}: {
  name: string;
  /** The value staged for the next publish, `null` for a staged removal, or nothing. */
  typed: string | null | undefined;
  stored: boolean;
  onType: (value: string) => void;
}): React.JSX.Element {
  const [shown, setShown] = useState(false);
  const value = typeof typed === 'string' ? typed : '';
  const placeholder =
    typed === null
      ? 'Removed with the next publish'
      : stored
        ? '•••••••••• stored — type to replace'
        : 'The secret, sealed apart from the document';
  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <Input
        aria-label={`Value of ${name || 'this secret'}`}
        type={shown ? 'text' : 'password'}
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onType(event.target.value)}
        style={{ fontFamily: font.mono, fontSize: 13, padding: '8px 56px 8px 10px', borderRadius: radius.md }}
      />
      <button
        type="button"
        aria-pressed={shown}
        disabled={!value}
        title={value ? undefined : stored ? 'A sealed value is never shown here — only a linked app reads it' : undefined}
        onClick={() => setShown(!shown)}
        style={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 0,
          padding: '2px 6px',
          fontFamily: 'inherit',
          fontSize: 12.5,
          color: value ? color.accentText : color.inkFaint,
          cursor: value ? 'pointer' : 'default'
        }}
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}
